// MIDI effects, heard — approximately (the note-stream half of device-graph.ts).
//
// Live's MIDI effects sit before the instrument on a MIDI lane and reshape
// the notes: Pitch transposes, Chord stacks intervals, Velocity rescales,
// Note Length rescales durations, Random offsets pitches by chance, and the
// Arpeggiator turns held notes into a pattern at a rate. The browser's
// preview applies the ones it recognises to a clip's notes before they are
// synthesised, driven by the Set's parameter values. Not Live's exact
// behaviour — Live's Arpeggiator alone has a dozen styles and a step
// sequencer — but the direction of every knob is right, and a lane whose
// chain carries a Pitch device at +12 previews an octave up, as it will
// play. Pure: notes in clip-relative beats in, notes out.

import type { WireNote } from "@shared/midi/notes.js";
import type { DeviceParam, DeviceState } from "@shared/sync/state.js";
import { INSTRUMENTS } from "@web-shared/lib/live-devices";

/** Devices that host an instrument without being one of the catalog's: the
 * walk stops at these too. A renamed instrument or a plug-in escapes this —
 * a MIDI effect after it is then heard here and not in Live; best effort. */
const INSTRUMENT_HOSTS: readonly string[] = ["Instrument Rack", "External Instrument", "Drum Rack"];

export type MidiStage =
  | { kind: "pitch"; semitones: number }
  | { kind: "chord"; shifts: number[] }
  | { kind: "velocity"; outLow: number; outHi: number }
  | { kind: "length"; gate: number }
  | { kind: "random"; chance: number; choices: number; scale: number }
  | { kind: "arp"; rateBeats: number; gate: number; style: "up" | "down" | "updown" };

function param(d: DeviceState, ...names: string[]): DeviceParam | undefined {
  const wanted = names.map((n) => n.toLowerCase());
  return d.params.find((p) => wanted.includes(p.name.toLowerCase()));
}

function norm(p: DeviceParam | undefined, fallback: number): number {
  if (!p || !(p.max > p.min)) return fallback;
  return Math.max(0, Math.min(1, (p.value - p.min) / (p.max - p.min)));
}

function isOn(d: DeviceState): boolean {
  const on = param(d, "Device On");
  return on === undefined || on.value > 0.5;
}

/** "1/16" → 0.25 beats, "1/8T" → a triplet eighth, "1/4D" → a dotted quarter. */
export function rateBeatsOf(label: string | undefined): number | undefined {
  if (!label) return undefined;
  const m = /^\s*1\s*\/\s*(\d+)\s*([TD]?)\s*$/i.exec(label);
  if (!m) return undefined;
  const div = Number(m[1]);
  if (!(div > 0)) return undefined;
  let beats = 4 / div;
  if (m[2]?.toUpperCase() === "T") beats *= 2 / 3;
  if (m[2]?.toUpperCase() === "D") beats *= 1.5;
  return beats;
}

/** The stages a MIDI lane's chain reduces to, in order — the MIDI effects
 * BEFORE the instrument, as Live runs them (a Pitch after Wavetable does
 * nothing there); audio effects contribute none (device-graph.ts). */
export function midiStagesFor(devices: readonly DeviceState[] | undefined): MidiStage[] {
  const out: MidiStage[] = [];
  for (const d of devices ?? []) {
    if (INSTRUMENTS.includes(d.name) || INSTRUMENT_HOSTS.includes(d.name)) break;
    if (!isOn(d)) continue;
    const stage = stageFor(d);
    if (stage) out.push(stage);
  }
  return out;
}

function stageFor(d: DeviceState): MidiStage | null {
  switch (d.name) {
    case "Pitch": {
      const p = param(d, "Pitch", "Transpose");
      return { kind: "pitch", semitones: p ? Math.round(Math.max(-48, Math.min(48, p.value))) : 0 };
    }
    case "Chord": {
      const shifts: number[] = [];
      for (let i = 1; i <= 6; i++) {
        const s = param(d, `Shift${i}`, `Shift ${i}`);
        if (s && s.value !== 0) shifts.push(Math.round(Math.max(-36, Math.min(36, s.value))));
      }
      return { kind: "chord", shifts };
    }
    case "Velocity": {
      const lo = param(d, "Out Low", "Out Lo");
      const hi = param(d, "Out Hi", "Out High");
      return { kind: "velocity", outLow: lo ? Math.round(Math.max(1, Math.min(127, lo.value))) : 1, outHi: hi ? Math.round(Math.max(1, Math.min(127, hi.value))) : 127 };
    }
    case "Note Length": {
      const g = param(d, "Gate");
      // A percentage when its range reaches past 2; a factor otherwise.
      const gate = g ? (g.max > 2 ? g.value / 100 : g.value) : 1;
      return { kind: "length", gate: Math.max(0.05, Math.min(4, gate)) };
    }
    case "Random": {
      const choices = param(d, "Choices");
      const scale = param(d, "Scale");
      return {
        kind: "random",
        chance: norm(param(d, "Chance"), 0.5),
        choices: choices ? Math.max(1, Math.min(24, Math.round(choices.value))) : 12,
        scale: scale ? Math.max(1, Math.min(24, Math.round(scale.value))) : 1,
      };
    }
    case "Arpeggiator": {
      const rate = param(d, "Synced Rate", "Rate");
      const label = rate?.items?.[Math.round(rate.value - rate.min)];
      const style = param(d, "Style");
      const styleName = style?.items?.[Math.round(style.value - style.min)]?.toLowerCase() ?? "up";
      const g = param(d, "Gate");
      return {
        kind: "arp",
        rateBeats: rateBeatsOf(label) ?? 0.25,
        gate: Math.max(0.05, Math.min(2, g ? (g.max > 2 ? g.value / 100 : g.value) : 0.5)),
        style: styleName.includes("down") ? (styleName.includes("up") ? "updown" : "down") : "up",
      };
    }
    default:
      return null;
  }
}

const clampPitch = (p: number): number => Math.max(0, Math.min(127, p));

/** Run the notes through the stages. `rng` decides Random's rolls;
 * `clipBeats` is where the clip ends (an arpeggio never runs past it);
 * `clipStartBeats` is where the clip sits in the song, so the arpeggio's
 * steps land on the SONG's grid, as Live's synced arp does. */
export function applyMidiStages(notes: readonly WireNote[], stages: readonly MidiStage[], rng: () => number = Math.random, clipBeats = Infinity, clipStartBeats = 0): WireNote[] {
  let out: WireNote[] = notes.map((n) => ({ ...n }));
  for (const s of stages) {
    switch (s.kind) {
      case "pitch":
        out = out.map((n) => ({ ...n, pitch: clampPitch(n.pitch + s.semitones) }));
        break;
      case "chord": {
        // A voice the clamp folds onto another is one voice, not two.
        const seen = new Set<string>();
        out = out
          .flatMap((n) => [n, ...s.shifts.map((shift) => ({ ...n, pitch: clampPitch(n.pitch + shift) }))])
          .filter((n) => {
            const key = `${n.pitch}@${n.startTime}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        break;
      }
      case "velocity":
        out = out.map((n) => ({ ...n, velocity: Math.round(s.outLow + (n.velocity / 127) * (s.outHi - s.outLow)) }));
        break;
      case "length":
        out = out.map((n) => ({ ...n, duration: Math.max(0.01, n.duration * s.gate) }));
        break;
      case "random":
        out = out.map((n) => {
          if (rng() >= s.chance) return n;
          const step = 1 + Math.floor(rng() * s.choices);
          return { ...n, pitch: clampPitch(n.pitch + step * s.scale) };
        });
        break;
      case "arp":
        out = arpeggiate(out, s, clipBeats, clipStartBeats);
        break;
    }
  }
  return out;
}

/** Held notes become a pattern: at every step of the rate — on the clip's
 * grid, as Live's synced arp is — the notes sounding then, ordered by the
 * style, one of them played for `gate` of the step. */
function arpeggiate(notes: readonly WireNote[], s: Extract<MidiStage, { kind: "arp" }>, clipBeats: number, clipStartBeats: number): WireNote[] {
  if (notes.length === 0) return [];
  // The song's grid in clip time: steps sit at multiples of the rate from
  // the song's origin, offset by where the clip starts.
  const phase = ((clipStartBeats % s.rateBeats) + s.rateBeats) % s.rateBeats;
  const first = Math.min(...notes.map((n) => n.startTime));
  const start = Math.ceil((first + phase) / s.rateBeats - 1e-9) * s.rateBeats - phase;
  const end = Math.min(clipBeats, Math.max(...notes.map((n) => n.startTime + n.duration)));
  const out: WireNote[] = [];
  let step = 0;
  let lastChord = "";
  for (let k = 0; ; k++) {
    const t = start + k * s.rateBeats;
    if (t >= end - 1e-9) break;
    const held = notes.filter((n) => n.startTime <= t + 1e-9 && n.startTime + n.duration > t + 1e-9).sort((a, b) => a.pitch - b.pitch);
    if (held.length === 0) continue;
    // A new chord restarts the pattern, as Live's arp does on retrigger.
    const chord = held.map((n) => n.pitch).join(",");
    if (chord !== lastChord) {
      step = 0;
      lastChord = chord;
    }
    const order = s.style === "down" ? [...held].reverse() : s.style === "updown" && held.length > 2 ? [...held, ...held.slice(1, -1).reverse()] : held;
    const pick = order[step % order.length]!;
    out.push({ ...pick, startTime: t, duration: Math.min(s.rateBeats * s.gate, end - t) });
    step++;
  }
  return out;
}
